import { create } from 'zustand';

interface CommandPaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

/**
 * Whether the palette is open.
 *
 * A store rather than component state because two unrelated places need it: the host
 * that renders the palette at the root route, and the shell's top bar, which has
 * advertised `⌘K` on a disabled input since the skeleton — *"rendered and wired to
 * nothing… disabled so it cannot be mistaken for working"*. Now that the key does
 * something, a dead control promising it is worse than no control.
 *
 * Two real consumers, which is the threshold rule 2.4 sets.
 *
 * It lives in `shared/store` beside the theme and the device preferences, not inside the
 * palette's own package, and that is not tidiness: the top bar belongs to `app-shell`,
 * and `app-shell`'s navigation registry is what the palette reads its destinations from.
 * A store owned by the palette would make the two features import each other — a cycle
 * that broke test collection outright the first time, before it could break anything
 * subtler.
 *
 * Not persisted: whether the palette is open is not a preference, and reopening it on a
 * page load would be a surprise rather than a convenience.
 */
export const useCommandPaletteStore = create<CommandPaletteState>()(
  (set, get) => ({
    open: false,
    setOpen: (open) => {
      set({ open });
    },
    toggle: () => {
      set({ open: !get().open });
    },
  }),
);
